import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../utils/api';
import { Button } from '../components/ui/button';
import { useCart } from '../contexts/CartContext';
import { useWishlist } from '../contexts/WishlistContext';
import { ShoppingCart, Minus, Plus, Truck, Shield, ArrowLeft, Heart } from 'lucide-react';
import { toast } from 'sonner';
import { SEO } from '../components/SEO';
import { RelatedProducts } from '../components/RelatedProducts';

const SITE_URL = 'https://sanatanbhavan.com';

export const ProductDetail = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const { addToCart } = useCart();
  const { addToWishlist, removeFromWishlist, isInWishlist } = useWishlist();

  useEffect(() => {
    api.get(`/products/${id}`).then(res => setProduct(res.data)).catch(() => toast.error('Product not found')).finally(() => setLoading(false));
  }, [id]);

  const handleAddToCart = () => { addToCart(product, quantity); toast.success(`${quantity} x ${product.name} added`); };
  const handleWishlist = () => {
    if (isInWishlist(product.id)) { removeFromWishlist(product.id); toast.success('Removed from wishlist'); }
    else { addToWishlist(product); toast.success('Added to wishlist'); }
  };

  if (loading) return <div className="max-w-7xl mx-auto px-4 py-12 flex justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div></div>;
  if (!product) return <div className="max-w-7xl mx-auto px-4 py-12 text-center"><p className="text-gray-500">Product not found</p><Link to="/products" className="text-orange-600 hover:underline mt-4 inline-block">← Back</Link></div>;

  const price = product.sale_price || product.price;
  const inWishlist = isInWishlist(product.id);

  // Resolve a fully-qualified image URL for the JSON-LD product schema
  const rawImg = product.images?.[0] || product.image_url || '';
  const productImage = rawImg.startsWith('/') ? `${SITE_URL}${rawImg}` : rawImg;

  // Build the SEO meta-description with this priority:
  // 1. product.meta_description (hand-tuned for Google snippet, ~155 chars)
  // 2. else, derive from on-page description (with price + brand suffix)
  const metaDesc = product.meta_description
    || `${product.name} — ₹${price}. ${(product.description || '').slice(0, 110)} Buy online at Sanatanbhavan.`;

  const productJsonLd = {
    '@context': 'https://schema.org/',
    '@type': 'Product',
    name: product.name,
    description: product.description || product.name,
    image: productImage,
    sku: product.sku,
    brand: { '@type': 'Brand', name: 'Sanatanbhavan' },
    category: product.category_name,
    offers: {
      '@type': 'Offer',
      url: `${SITE_URL}/product/${product.id}`,
      priceCurrency: 'INR',
      price: String(price),
      availability: product.stock > 0 ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
      itemCondition: 'https://schema.org/NewCondition',
    },
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <SEO
        title={product.name}
        description={metaDesc}
        keywords={product.meta_keywords}
        image={productImage}
        type="product"
        jsonLd={productJsonLd}
      />
      <Link to="/products" className="inline-flex items-center gap-2 text-orange-600 hover:underline mb-6"><ArrowLeft size={18} /> Back to Products</Link>
      <div className="grid md:grid-cols-2 gap-8">
        <div className="bg-white rounded-2xl overflow-hidden shadow-sm relative">
          <img src={product.images?.[0] || 'https://images.pexels.com/photos/5702290/pexels-photo-5702290.jpeg?auto=compress&cs=tinysrgb&w=600'} alt={product.name} className="w-full aspect-square object-cover" />
          <button onClick={handleWishlist} className={`absolute top-4 right-4 p-3 rounded-full shadow-lg ${inWishlist ? 'bg-pink-500 text-white' : 'bg-white text-gray-600 hover:text-pink-500'}`}>
            <Heart size={24} fill={inWishlist ? 'currentColor' : 'none'} />
          </button>
        </div>
        <div>
          <p className="text-orange-600 font-medium mb-2">{product.category_name}</p>
          <h1 className="text-3xl font-bold text-gray-800 mb-4">{product.name}</h1>
          <div className="flex items-baseline gap-3 mb-6">
            <span className="text-3xl font-bold text-orange-600">₹{price}</span>
            {product.sale_price && (<><span className="text-xl text-gray-400 line-through">₹{product.price}</span><span className="bg-green-100 text-green-700 text-sm px-2 py-1 rounded">{Math.round((1 - product.sale_price / product.price) * 100)}% OFF</span></>)}
          </div>
          <p className="text-gray-600 mb-6">{product.description}</p>
          <div className="grid grid-cols-2 gap-4 mb-6 text-sm">
            {product.material && <div><span className="text-gray-500">Material:</span> <span className="font-medium">{product.material}</span></div>}
            {product.weight && <div><span className="text-gray-500">Weight:</span> <span className="font-medium">{product.weight}</span></div>}
            <div><span className="text-gray-500">SKU:</span> <span className="font-medium">{product.sku}</span></div>
          </div>
          <div className="mb-6">{product.stock > 10 ? <span className="text-green-600 font-medium">✓ In Stock</span> : product.stock > 0 ? <span className="text-orange-600 font-medium">Only {product.stock} left!</span> : <span className="text-red-600 font-medium">Out of Stock</span>}</div>
          {product.stock > 0 && (
            <div className="flex items-center gap-4 mb-6">
              <div className="flex items-center border rounded-lg">
                <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="p-3 hover:bg-gray-100"><Minus size={18} /></button>
                <span className="px-4 font-medium">{quantity}</span>
                <button onClick={() => setQuantity(Math.min(product.stock, quantity + 1))} className="p-3 hover:bg-gray-100"><Plus size={18} /></button>
              </div>
              <Button size="lg" onClick={handleAddToCart} className="bg-orange-600 hover:bg-orange-700 flex-1" data-testid="add-to-cart-btn"><ShoppingCart className="mr-2" size={20} />Add to Cart - ₹{price * quantity}</Button>
            </div>
          )}
          <div className="border-t pt-6 space-y-3">
            <div className="flex items-center gap-3 text-sm"><Truck className="text-orange-600" size={20} /><span>Free shipping on orders above ₹500</span></div>
            <div className="flex items-center gap-3 text-sm"><Shield className="text-orange-600" size={20} /><span>100% Authentic & Genuine Products</span></div>
          </div>
          <a href={`https://wa.me/919831072381?text=Hi, I'm interested in: ${product.name} (₹${price})`} target="_blank" rel="noopener noreferrer" className="mt-6 inline-flex items-center gap-2 bg-green-600 text-white px-6 py-3 rounded-full hover:bg-green-700 transition">
            <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
            Order via WhatsApp
          </a>
        </div>
      </div>
      <RelatedProducts productId={product.id} limit={4} />
    </div>
  );
};
